import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Calendar,
    Edit,
    Eye,
    Plus,
    RotateCcw,
    Trash2,
    User as UserIcon,
    Users,
    Building2,
    UserCircle,
    Target,
} from 'lucide-react';
import { useDeleteUser, useReactivateUser, useUsers } from '@/api/users/use-users.ts';
import { User } from '@/models/user.ts';
import { Button } from '../../components/ui/button';
import { GenericDataTable } from '@/components/shared/tables';
import { Column } from '@/components/ui/data-table';
import { type FilterPreset } from '@/components/shared/filters';
import { useSnackbar } from 'notistack';
import { useExportUsers } from '../../api/export/use-export';
import DateTimeFormatter from '@/components/shared/DateTimeFormatter';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import HierarchyChart from './components/HierarchyChart';
import { useUrlState } from '@/hooks/shared';
import { useConfirm } from '@/hooks/useConfirm';
import { usePermissions } from '@/hooks/usePermissions';
import { ModulePermission, PermissionAction } from '@/types/permissions';
import { PageContainer, PageHeader } from '@/components/shared/layout';
import { EmptyState } from '@/components/shared/EmptyState';
import { StatusBadge } from '@/components/shared/StatusBadge';

const UsersPage: React.FC = () => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { hasPermission } = usePermissions();
    const confirm = useConfirm();

    // Use URL-based state management
    const {
        filters,
        sort,
        pagination,
        updateFilters,
        clearFilters,
        toggleSort,
        goToPage,
        updatePagination,
    } = useUrlState({
        sort: { key: 'name', direction: 'asc' },
        pagination: { page: 1, pageSize: 25 },
    });

    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

    const { data: usersResponse, isLoading } = useUsers({
        page: pagination.page,
        per_page: pagination.pageSize,
        sort_by: sort.key,
        sort_direction: sort.direction,
        ...Object.fromEntries(
            Object.entries(filters).filter(
                ([, value]) =>
                    value !== undefined && value !== '' && value !== null && value !== 'all'
            )
        ),
    });

    const users = usersResponse?.data || [];

    const deleteUserMutation = useDeleteUser();
    const reactivateUserMutation = useReactivateUser();

    const handleAddUser = () => {
        navigate('/users/create');
    };

    const handleUserClick = (user: User) => {
        navigate(`/users/${user.id}/edit`);
    };

    const handleSort = (key: keyof User) => {
        toggleSort(key as string);
    };

    const handleFilterChange = (newFilters: any) => {
        updateFilters(newFilters);
    };

    const handleClearFilters = () => {
        clearFilters();
    };

    /** Drives the empty state: "no matches" reads differently from "nothing yet". */
    const hasActiveFilters = useMemo(
        () => Object.values(filters ?? {}).some(v => v !== '' && v !== null && v !== undefined),
        [filters]
    );

    const handlePageSizeChange = (newPageSize: number) => {
        updatePagination({ page: 1, pageSize: newPageSize });
    };

    const exportUsersMutation = useExportUsers(
        () => {
            enqueueSnackbar('Users exported successfully', { variant: 'success' });
        },
        (error: Error) => {
            console.error('Export failed:', error);
            enqueueSnackbar('Failed to export users', { variant: 'error' });
        }
    );

    const handleExportUsers = () => {
        const exportFilters =
            selectedUsers.length > 0 ? { selected_ids: selectedUsers } : { ...filters };

        exportUsersMutation.mutate({
            format: 'csv',
            filters: exportFilters,
        });
    };

    // Save filter preset
    const handleSavePreset = (preset: Omit<FilterPreset, 'id'>) => {
        enqueueSnackbar(`Filter preset "${preset.name}" saved`, { variant: 'success' });
    };

    // Filter presets
    const filterPresets: FilterPreset[] = useMemo(
        () => [
            {
                id: 'active-employees',
                name: 'Active Employees',
                description: 'All active employees',
                isDefault: true,
                filters: {
                    status: 'active',
                },
            },
            {
                id: 'managers',
                name: 'Managers',
                description: 'Employees with management titles',
                isDefault: true,
                filters: {
                    title: 'manager',
                },
            },
            {
                id: 'sales-team',
                name: 'Sales Team',
                description: 'Sales and related roles',
                isDefault: true,
                filters: {
                    title: 'sales',
                },
            },
            {
                id: 'support-team',
                name: 'Support Team',
                description: 'Support and customer service',
                isDefault: true,
                filters: {
                    title: 'support',
                },
            },
            {
                id: 'suspended-users',
                name: 'Suspended Users',
                description: 'Users with suspended status',
                isDefault: true,
                filters: {
                    status: 'suspended',
                },
            },
            {
                id: 'recent-users',
                name: 'Recently Added',
                description: 'Users created in the last 30 days',
                isDefault: true,
                filters: {
                    created_after: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
                        .toISOString()
                        .split('T')[0],
                },
            },
        ],
        []
    );

    // Define columns for the DataTable
    const columns: Column<User>[] = [
        {
            id: 'name',
            label: 'Name',
            key: 'name',
            sortable: true,
            width: '200px',
            render: (value, user) => (
                <div>
                    <div className="font-medium">{value}</div>
                    <div className="text-sm text-muted-foreground">
                        {user.title && <span className="me-2">{user.title}</span>}
                        ID: {user.id.slice(0, 8)}...
                    </div>
                </div>
            ),
        },
        {
            id: 'email',
            label: 'Email',
            key: 'email',
            sortable: true,
            width: '200px',
            render: value => (
                <div className="flex items-center gap-1">
                    <span className="text-sm">{value}</span>
                </div>
            ),
        },
        {
            id: 'supervisor',
            label: 'Supervisor',
            key: 'supervisor_id',
            sortable: true,
            width: '150px',
            render: (_, user) => (
                <div className="flex items-center gap-1">
                    {user.supervisor ? (
                        <>
                            <UserIcon className="h-3 w-3 text-muted-foreground" />
                            <div>
                                <div className="text-sm font-medium">{user.supervisor.name}</div>
                                {user.supervisor.title && (
                                    <div className="text-xs text-muted-foreground">
                                        {user.supervisor.title}
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <span className="text-sm text-muted-foreground">No supervisor</span>
                    )}
                </div>
            ),
        },
        {
            id: 'team_size',
            label: 'Team Size',
            key: 'subordinates_count',
            sortable: true,
            width: '100px',
            render: (_, user) => (
                <div className="flex items-center gap-1">
                    <Users className="h-3 w-3 text-muted-foreground" />
                    <span className="text-sm">{user.subordinates_count || 0}</span>
                </div>
            ),
        },
        {
            id: 'customers_count',
            label: 'Assigned Customers',
            key: 'customers_count',
            sortable: false,
            width: '120px',
            render: (_, user) => (
                <div className="flex items-center gap-1">
                    <UserCircle className="h-3 w-3 text-muted-foreground" />
                    <span className="text-sm">{user.customers_count ?? 0}</span>
                </div>
            ),
        },
        {
            id: 'leads_count',
            label: 'Assigned Leads',
            key: 'leads_count',
            sortable: false,
            width: '110px',
            render: (_, user) => (
                <div className="flex items-center gap-1">
                    <Target className="h-3 w-3 text-muted-foreground" />
                    <span className="text-sm">{user.leads_count ?? 0}</span>
                </div>
            ),
        },
        {
            id: 'status',
            label: 'Status',
            key: 'status',
            sortable: true,
            width: '120px',
            render: (_, user) => (
                <StatusBadge
                    status={user.status}
                    kind="user"
                    label={user.status === 'inactive' ? 'Deactivated' : undefined}
                />
            ),
        },
        {
            id: 'role',
            label: 'Role',
            key: 'role',
            sortable: true,
            width: '120px',
            render: (_, user) => (
                <div className="flex items-center gap-1">
                    <Building2 className="h-3 w-3 text-muted-foreground" />
                    <span className="text-sm capitalize">{user.role?.name || 'user'}</span>
                </div>
            ),
        },
        {
            id: 'created_at',
            label: 'Created',
            key: 'created_at',
            sortable: true,
            width: '180px',
            render: value => (
                <DateTimeFormatter
                    date={value}
                    showIcon={true}
                    icon={<Calendar className="h-3 w-3 text-muted-foreground" />}
                    className="text-sm"
                />
            ),
        },
        {
            id: 'updated_at',
            label: 'Updated',
            key: 'updated_at',
            sortable: true,
            width: '120px',
            render: value => <DateTimeFormatter date={value} className="text-sm" />,
        },
        {
            id: 'actions',
            label: 'Actions',
            key: 'id',
            sortable: false,
            width: '120px',
            render: (_, user) => (
                <div className="flex items-center gap-1">
                    <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label="View employee"
                        title="View employee"
                        onClick={event => {
                            event.stopPropagation();
                            navigate(`/users/${user.id}`);
                        }}
                    >
                        <Eye className="h-4 w-4" />
                    </Button>
                    {hasPermission(ModulePermission.USERS, PermissionAction.EDIT) && (
                        <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label="Edit employee"
                            title="Edit employee"
                            onClick={event => {
                                event.stopPropagation();
                                navigate(`/users/${user.id}/edit`);
                            }}
                        >
                            <Edit className="h-4 w-4" />
                        </Button>
                    )}
                    {user.status === 'inactive' &&
                        hasPermission(ModulePermission.USERS, PermissionAction.EDIT) && (
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                aria-label="Reactivate employee"
                                title="Reactivate employee"
                                onClick={async event => {
                                    event.stopPropagation();
                                    try {
                                        await reactivateUserMutation.mutateAsync(user.id);
                                        enqueueSnackbar('Employee reactivated successfully', {
                                            variant: 'success',
                                        });
                                    } catch (error) {
                                        enqueueSnackbar('Failed to reactivate employee', {
                                            variant: 'error',
                                        });
                                    }
                                }}
                            >
                                <RotateCcw className="h-4 w-4" />
                            </Button>
                        )}
                    {user.status !== 'inactive' &&
                        hasPermission(ModulePermission.USERS, PermissionAction.DELETE) && (
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                aria-label="Deactivate employee"
                                title="Deactivate employee"
                                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                                onClick={async event => {
                                    event.stopPropagation();
                                    const ok = await confirm({
                                        title: 'Deactivate employee?',
                                        description: `${user.name} will no longer be able to sign in and will be hidden from assignee lists. Their notes, leads and customers are kept, and you can reactivate them at any time.`,
                                        confirmLabel: 'Deactivate',
                                        variant: 'destructive',
                                    });
                                    if (!ok) return;

                                    try {
                                        await deleteUserMutation.mutateAsync(user.id);
                                        enqueueSnackbar('Employee deactivated successfully', {
                                            variant: 'success',
                                        });
                                    } catch (error) {
                                        enqueueSnackbar('Failed to deactivate employee', {
                                            variant: 'error',
                                        });
                                    }
                                }}
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        )}
                </div>
            ),
        },
    ];

    // Define filter fields for the GenericDataTable
    const filterFields = [
        {
            key: 'search',
            label: 'Search',
            type: 'text' as const,
            placeholder: 'Search employees...',
        },
        {
            key: 'title',
            label: 'Title',
            type: 'text' as const,
            placeholder: 'Search by job title...',
        },
        {
            key: 'status',
            label: 'Status',
            type: 'select' as const,
            options: [
                { value: 'all', label: 'All Status' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Deactivated' },
                { value: 'suspended', label: 'Suspended' },
            ],
        },
        {
            key: 'include_deactivated',
            label: 'Deactivated employees',
            type: 'select' as const,
            options: [
                { value: 'all', label: 'Hide deactivated' },
                { value: '1', label: 'Show deactivated' },
            ],
        },
        {
            key: 'role',
            label: 'Role',
            type: 'select' as const,
            options: [
                { value: 'all', label: 'All Roles' },
                { value: 'admin', label: 'Admin' },
                { value: 'user', label: 'User' },
                { value: 'manager', label: 'Manager' },
            ],
        },
    ];

    return (
        <PageContainer fullHeight>
            <PageHeader
                title="Employees"
                description="Manage system employees, roles, and organizational hierarchy"
                actions={
                    hasPermission(ModulePermission.USERS, PermissionAction.CREATE) ? (
                        <Button onClick={handleAddUser}>
                            <Plus className="h-4 w-4" />
                            Add Employee
                        </Button>
                    ) : undefined
                }
            />

            <Tabs defaultValue="list" className="flex min-h-0 flex-1 flex-col gap-4">
                <TabsList className="shrink-0">
                    <TabsTrigger value="list">Employee List</TabsTrigger>
                    <TabsTrigger value="hierarchy">Organization Chart</TabsTrigger>
                </TabsList>

                <TabsContent value="list" className="mt-0 flex min-h-0 flex-1 flex-col">
                    {/* Generic Data Table */}
                    <GenericDataTable<User>
                        data={users}
                        columns={columns}
                        visibleColumns={columns.map(col => col.id)}
                        filters={filters}
                        filterFields={filterFields}
                        presets={filterPresets}
                        isLoading={isLoading}
                        selectedItems={selectedUsers}
                        onSelectionChange={setSelectedUsers}
                        getItemId={user => user.id}
                        sortConfig={{
                            key: sort.key as keyof User,
                            direction: sort.direction,
                        }}
                        onSort={handleSort}
                        totalItems={usersResponse?.meta?.total || users.length}
                        totalPages={usersResponse?.meta?.last_page || 1}
                        currentPage={pagination.page}
                        pageSize={pagination.pageSize}
                        onPageChange={goToPage}
                        onPageSizeChange={handlePageSizeChange}
                        onExport={handleExportUsers}
                        showExportButton={hasPermission(
                            ModulePermission.USERS,
                            PermissionAction.EXPORT
                        )}
                        onRowClick={handleUserClick}
                        onFilterChange={handleFilterChange}
                        onClearFilters={handleClearFilters}
                        onLoadPreset={preset => handleFilterChange(preset.filters)}
                        onSavePreset={handleSavePreset}
                        onColumnToggle={() => {}}
                        onResetColumns={() => {}}
                        emptyMessage="No employees found"
                        loadingMessage="Loading employees..."
                        showRowClick={true}
                        stickyLayout
                        emptyState={
                            <EmptyState
                                icon={Users}
                                title="No employees found"
                                description={
                                    hasActiveFilters
                                        ? 'No employee matches the current filters. Try clearing them.'
                                        : 'Employees will appear here once they are added.'
                                }
                                action={
                                    hasActiveFilters ? (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleClearFilters}
                                        >
                                            Clear filters
                                        </Button>
                                    ) : hasPermission(
                                          ModulePermission.USERS,
                                          PermissionAction.CREATE
                                      ) ? (
                                        <Button size="sm" onClick={handleAddUser}>
                                            <Plus className="h-4 w-4" />
                                            Add Employee
                                        </Button>
                                    ) : undefined
                                }
                            />
                        }
                    />
                </TabsContent>

                <TabsContent value="hierarchy" className="mt-0 min-h-0 flex-1 overflow-y-auto">
                    <HierarchyChart users={users} />
                </TabsContent>
            </Tabs>
        </PageContainer>
    );
};

export default UsersPage;
